import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchBrand, getBrandLogo } from '@/lib/cms';
import NewsletterModal from '@/components/NewsletterModal';
import { openCookieSettings } from '@/lib/consent';

const Footer: React.FC = () => {
  const [logoWhiteSrc, setLogoWhiteSrc] = useState('https://media.adventive.com/brand/logos/wordmark/png/adventive_wordmark-white.png');
  const [socialUrls, setSocialUrls] = useState<Record<string, string>>({});
  const [newsletterOpen, setNewsletterOpen] = useState(false);

  useEffect(() => {
    fetchBrand().then(brand => {
      if (!brand) return;
      if (brand.logos?.length) {
        const white = getBrandLogo(brand.logos, 'wordmark', 'white', 'png');
        if (white) setLogoWhiteSrc(white);
      }
      if (brand.social_links?.length) {
        const urls: Record<string, string> = {};
        for (const link of brand.social_links) {
          if (link.enabled && link.url) urls[link.platform] = link.url;
        }
        setSocialUrls(urls);
      }
    }).catch(() => { /* keep defaults */ });
  }, []);
  const solutions = [
    { name: 'Publishers', href: '/solutions/publishers' },
    { name: 'Agencies', href: '/solutions/agencies' },
    { name: 'Brands', href: '/solutions/brands' },
  ];

  const industries = [
    { name: 'Fashion', href: '/solutions/fashion' },
    { name: 'Travel & Tourism', href: '/solutions/travel-and-tourism' },
    { name: 'Automotive', href: '/solutions/automotive' },
    { name: 'Entertainment & Media', href: '/solutions/entertainment-and-media' },
    { name: 'Consumer Electronics', href: '/solutions/consumer-electronics' },
    { name: 'Financial Services', href: '/solutions/financial-services' },
  ];

  const platform = [
    { name: 'Platform Overview', href: '/platform' },
    { name: 'Managed Services', href: '/managed-services' },
    { name: 'Interactive Ad Builder', href: '/platform#builder' },
    { name: 'Performance Insights', href: '/platform#insights' },
  ];


  const resources = [
    { name: 'All Resources', href: '/resources' },
    { name: 'Blog', href: '/blog' },
    { name: 'Help Center', href: 'https://help.adventive.com', target: '_blank' },
    { name: 'Legal', href: '/resources/legal' },
    { name: 'Subscribe to Newsletter', href: '#newsletter' },
  ];

  const company = [
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'Careers', href: '/careers' },
  ];




  return (
    <>
    <footer className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-slate-900 dark:bg-[#0a0a0f]">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        {/* Gradient accent at top */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#635bff]/50 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-6 lg:gap-6">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="inline-block">
              <img
                src={logoWhiteSrc}
                alt="Adventive"
                className="h-5"
              />
            </Link>


            <p className="mt-4 text-sm text-slate-400 max-w-xs leading-relaxed">
              Building solutions that help publishers and brands create high impact ads, and capture more revenue.
            </p>
            <div className="mt-6 flex space-x-4">
              <a
                href={socialUrls.facebook || 'https://www.facebook.com/adventiveinc/'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Facebook"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#635bff]/20 border border-white/10 hover:border-[#635bff]/30 flex items-center justify-center text-slate-400 hover:text-[#635bff] transition-all"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 640 640">
                  <path d="M240 363.3L240 576L356 576L356 363.3L442.5 363.3L460.5 265.5L356 265.5L356 230.9C356 179.2 376.3 159.4 428.7 159.4C445 159.4 458.1 159.8 465.7 160.6L465.7 71.9C451.4 68 416.4 64 396.2 64C289.3 64 240 114.5 240 223.4L240 265.5L174 265.5L174 363.3L240 363.3z" />
                </svg>
              </a>
              <a
                href={socialUrls.instagram || 'https://www.instagram.com/adventiveinc'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on Instagram"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#635bff]/20 border border-white/10 hover:border-[#635bff]/30 flex items-center justify-center text-slate-400 hover:text-[#635bff] transition-all"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 640 640">
                  <path d="M320.3 205C256.8 204.8 205.2 256.2 205 319.7C204.8 383.2 256.2 434.8 319.7 435C383.2 435.2 434.8 383.8 435 320.3C435.2 256.8 383.8 205.2 320.3 205zM319.7 245.4C360.9 245.2 394.4 278.5 394.6 319.7C394.8 360.9 361.5 394.4 320.3 394.6C279.1 394.8 245.6 361.5 245.4 320.3C245.2 279.1 278.5 245.6 319.7 245.4zM413.1 200.3C413.1 185.5 425.1 173.5 439.9 173.5C454.7 173.5 466.7 185.5 466.7 200.3C466.7 215.1 454.7 227.1 439.9 227.1C425.1 227.1 413.1 215.1 413.1 200.3zM542.8 227.5C541.1 191.6 532.9 159.8 506.6 133.6C480.4 107.4 448.6 99.2 412.7 97.4C375.7 95.3 264.8 95.3 227.8 97.4C192 99.1 160.2 107.3 133.9 133.5C107.6 159.7 99.5 191.5 97.7 227.4C95.6 264.4 95.6 375.3 97.7 412.3C99.4 448.2 107.6 480 133.9 506.2C160.2 532.4 191.9 540.6 227.8 542.4C264.8 544.5 375.7 544.5 412.7 542.4C448.6 540.7 480.4 532.5 506.6 506.2C532.8 480 541 448.2 542.8 412.3C544.9 375.3 544.9 264.5 542.8 227.5zM495 452C487.2 471.6 472.1 486.7 452.4 494.6C422.9 506.3 352.9 503.6 320.3 503.6C287.7 503.6 217.6 506.2 188.2 494.6C168.6 486.8 153.5 471.7 145.6 452C133.9 422.5 136.6 352.5 136.6 319.9C136.6 287.3 134 217.2 145.6 187.8C153.4 168.2 168.5 153.1 188.2 145.2C217.7 133.5 287.7 136.2 320.3 136.2C352.9 136.2 423 133.6 452.4 145.2C472 153 487.1 168.1 495 187.8C506.7 217.3 504 287.3 504 319.9C504 352.5 506.7 422.6 495 452z" />
                </svg>
              </a>
              {/* X (Twitter) — temporarily hidden
              <a
                href={socialUrls.twitter || 'https://x.com/adventive'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect with us on X"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#635bff]/20 border border-white/10 hover:border-[#635bff]/30 flex items-center justify-center text-slate-400 hover:text-[#635bff] transition-all"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 640 640">
                  <path d="M453.2 112L523.8 112L369.6 288.2L551 528L409 528L297.7 382.6L170.5 528L99.8 528L264.7 339.5L90.8 112L236.4 112L336.9 244.9L453.2 112zM428.4 485.8L467.5 485.8L215.1 152L173.1 152L428.4 485.8z" />
                </svg>
              </a>
              */}
              {/* YouTube — temporarily hidden
              <a
                href={socialUrls.youtube || 'https://youtube.com/@beadventive'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow us on YouTube"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#635bff]/20 border border-white/10 hover:border-[#635bff]/30 flex items-center justify-center text-slate-400 hover:text-[#635bff] transition-all"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 640 640">
                  <path d="M581.7 188.1C575.5 164.4 556.9 145.8 533.4 139.5C490.9 128 320.1 128 320.1 128C320.1 128 149.3 128 106.7 139.5C83.2 145.8 64.7 164.4 58.4 188.1C47 231 47 320.4 47 320.4C47 320.4 47 409.8 58.4 452.7C64.7 476.3 83.2 494.2 106.7 500.5C149.3 512 320.1 512 320.1 512C320.1 512 490.9 512 533.5 500.5C557 494.2 575.5 476.3 581.8 452.7C593.2 409.8 593.2 320.4 593.2 320.4C593.2 320.4 593.2 231 581.8 188.1zM264.2 401.6L264.2 239.2L406.9 320.4L264.2 401.6z" />
                </svg>
              </a>
              */}
              <a
                href={socialUrls.linkedin || 'https://linkedin.com/company/adventive-inc'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Connect with us on LinkedIn"
                className="w-10 h-10 rounded-full bg-white/5 hover:bg-[#635bff]/20 border border-white/10 hover:border-[#635bff]/30 flex items-center justify-center text-slate-400 hover:text-[#635bff] transition-all"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 640 640">
                  <path d="M196.3 512L103.4 512L103.4 212.9L196.3 212.9L196.3 512zM149.8 172.1C120.1 172.1 96 147.5 96 117.8C96 103.5 101.7 89.9 111.8 79.8C121.9 69.7 135.6 64 149.8 64C164 64 177.7 69.7 187.8 79.8C197.9 89.9 203.6 103.6 203.6 117.8C203.6 147.5 179.5 172.1 149.8 172.1zM543.9 512L451.2 512L451.2 366.4C451.2 331.7 450.5 287.2 402.9 287.2C354.6 287.2 347.2 324.9 347.2 363.9L347.2 512L254.4 512L254.4 212.9L343.5 212.9L343.5 253.7L344.8 253.7C357.2 230.2 387.5 205.4 432.7 205.4C526.7 205.4 544 267.3 544 347.7L544 512L543.9 512z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Solutions</h3>
            <ul className="mt-4 space-y-3">
              {solutions.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-sm text-slate-400 hover:text-[#635bff] transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Industries</h3>
            <ul className="mt-4 space-y-3">
              {industries.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-sm text-slate-400 hover:text-[#635bff] transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Platform */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Platform</h3>
            <ul className="mt-4 space-y-3">
              {platform.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-sm text-slate-400 hover:text-[#635bff] transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Resources</h3>
            <ul className="mt-4 space-y-3">
              {resources.map((item) => (
                <li key={item.name}>
                  {item.href === '#newsletter' ? (
                    <button
                      onClick={() => setNewsletterOpen(true)}
                      className="text-sm text-slate-400 hover:text-[#635bff] transition-colors"
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      target={item.target}
                      className="text-sm text-slate-400 hover:text-[#635bff] transition-colors"
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-white">Company</h3>
            <ul className="mt-4 space-y-3">
              {company.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-sm text-slate-400 hover:text-[#635bff] transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>


        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-slate-500" data-mixed-content="true">
              © {new Date().getFullYear()} Adventive Tech Inc. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link
                to="/legal/privacy-policy"
                className="text-sm text-slate-500 hover:text-[#635bff] transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/legal/terms-conditions"
                className="text-sm text-slate-500 hover:text-[#635bff] transition-colors"
              >
                Terms of Service
              </Link>
              <button
                type="button"
                onClick={openCookieSettings}
                className="text-sm text-slate-500 hover:text-[#635bff] transition-colors"
              >
                Cookie Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>

    {newsletterOpen && <NewsletterModal onClose={() => setNewsletterOpen(false)} />}
    </>
  );
};

export default Footer;
